import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ArrowLeft,
  Clock,
  Calendar,
  Plus,
  Save,
  CheckCircle,
  AlertCircle,
  Image as ImageIcon,
  Trash2
} from 'lucide-react';

interface TimesheetEntry {
  id: string;
  date: string;
  project: string;
  hours: number;
  description: string;
  status: 'draft' | 'submitted' | 'approved';
}

export default function Timesheet() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { theme, setTheme, setThemeTransient } = useTheme();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoadingLogo, setIsLoadingLogo] = useState(true);
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeAssignments, setActiveAssignments] = useState<any[]>([]);

  useEffect(() => {
    setThemeTransient('light');
  }, [setThemeTransient]);

  useEffect(() => {
    if (user) {
      fetchActiveAssignments();
      fetchTimesheetEntries();
    }
    fetchLogo();
  }, [user?.id]);

  const fetchLogo = async () => {
    try {
      setIsLoadingLogo(true);
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'logo_url')
        .maybeSingle();

      if (!error && data?.value) {
        const img = new Image();
        img.onload = () => {
          setLogoUrl(data.value);
          setIsLoadingLogo(false);
        };
        img.onerror = () => setIsLoadingLogo(false);
        img.src = data.value;
      } else {
        setIsLoadingLogo(false);
      }
    } catch (err) {
      setIsLoadingLogo(false);
    }
  };

  const fetchActiveAssignments = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('consultant_assignments')
        .select(`
          id,
          project_name,
          companies (
            name
          )
        `)
        .eq('consultant_id', user.id)
        .eq('status', 'active');

      if (error) throw error;
      setActiveAssignments(data || []);
    } catch (error) {
      console.error('Error fetching active assignments:', error);
    }
  };

  const fetchTimesheetEntries = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setEntries([]);
    } catch (error) {
      console.error('Error fetching timesheet entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const addNewEntry = () => {
    const newEntry: TimesheetEntry = {
      id: Date.now().toString(),
      date: new Date().toISOString().split('T')[0],
      project: '',
      hours: 0,
      description: '',
      status: 'draft'
    };
    setEntries([newEntry, ...entries]);
  };

  const updateEntry = (id: string, field: keyof TimesheetEntry, value: any) => {
    setEntries(entries.map(entry =>
      entry.id === id ? { ...entry, [field]: value } : entry
    ));
  };

  const deleteEntry = (id: string) => {
    setEntries(entries.filter(entry => entry.id !== id));
  };

  const submitTimesheet = () => {
    const draftEntries = entries.filter(e => e.status === 'draft');
    if (draftEntries.length === 0) {
      setMessage({ type: 'error', text: 'No draft entries to submit' });
      return;
    }

    setEntries(entries.map(entry =>
      entry.status === 'draft' ? { ...entry, status: 'submitted' } : entry
    ));
    setMessage({ type: 'success', text: 'Timesheet submitted successfully!' });
  };

  const getTotalHours = () => {
    return entries.reduce((total, entry) => total + (Number(entry.hours) || 0), 0);
  };

  const getDraftHours = () => {
    return entries
      .filter(e => e.status === 'draft')
      .reduce((total, entry) => total + (Number(entry.hours) || 0), 0);
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: 'bg-yellow-100 text-yellow-800',
      submitted: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800'
    };
    return badges[status as keyof typeof badges] || badges.draft;
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      {/* Header */}
      <nav className="fixed w-full z-50 bg-black backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="text-white -mt-4 -mb-4 cursor-default select-none">
              {isLoadingLogo ? (
                <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : logoUrl ? (
                <img
                  src="/1.png"
                  alt="Perplexity"
                  className="h-40 w-auto object-contain"
                />
              ) : (
                <ImageIcon size={32} className="text-primary" />
              )}
            </div>
            <button
              onClick={() => navigate('/consultant/dashboard')}
              className="text-white hover:text-primary transition-colors flex items-center"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">Submit Time</h1>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Log your hours and submit timesheets for approval
            </p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className={`p-6 rounded-xl ${
              theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Total Hours</p>
                  <p className="text-3xl font-bold mt-2">{getTotalHours()}</p>
                </div>
                <Clock className="w-12 h-12 text-primary" />
              </div>
            </div>

            <div className={`p-6 rounded-xl ${
              theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Draft Hours</p>
                  <p className="text-3xl font-bold mt-2">{getDraftHours()}</p>
                </div>
                <Calendar className="w-12 h-12 text-yellow-500" />
              </div>
            </div>

            <div className={`p-6 rounded-xl ${
              theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>This Week</p>
                  <p className="text-3xl font-bold mt-2">40</p>
                </div>
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
            </div>
          </div>

          {/* Messages */}
          {message && (
            <div
              className={`mb-6 p-4 rounded-lg flex items-center ${
                message.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}
            >
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={addNewEntry}
              className="flex items-center bg-primary text-black font-semibold px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Entry
            </button>
            <button
              onClick={submitTimesheet}
              className={`flex items-center font-semibold px-6 py-3 rounded-lg transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              <Save className="w-5 h-5 mr-2" />
              Submit Timesheet
            </button>
          </div>

          {/* Timesheet Entries */}
          <div className="space-y-4">
            {entries.length === 0 ? (
              <div className={`text-center py-12 rounded-xl ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              }`}>
                <Clock className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-xl font-semibold mb-2">No timesheet entries</h3>
                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                  Click "Add Entry" to start logging your hours
                </p>
              </div>
            ) : (
              entries.map((entry) => (
                <div
                  key={entry.id}
                  className={`p-6 rounded-xl ${
                    theme === 'dark'
                      ? 'bg-gray-800 border border-gray-700'
                      : 'bg-white border border-gray-200 shadow-sm'
                  }`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                    {/* Date */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-2">Date</label>
                      <input
                        type="date"
                        value={entry.date}
                        onChange={(e) => updateEntry(entry.id, 'date', e.target.value)}
                        disabled={entry.status !== 'draft'}
                        className={`w-full px-4 py-2 rounded-lg border ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50`}
                      />
                    </div>

                    {/* Project */}
                    <div className="md:col-span-4">
                      <label className="block text-sm font-medium mb-2">Project</label>
                      {activeAssignments.length > 0 ? (
                        <select
                          value={entry.project}
                          onChange={(e) => updateEntry(entry.id, 'project', e.target.value)}
                          disabled={entry.status !== 'draft'}
                          className={`w-full px-4 py-2 rounded-lg border ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-300 text-gray-900'
                          } focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50`}
                        >
                          <option value="">Select project</option>
                          {activeAssignments.map((assignment) => (
                            <option
                              key={assignment.id}
                              value={`${assignment.project_name} - ${assignment.companies?.name || 'Client'}`}
                            >
                              {assignment.project_name} - {assignment.companies?.name || 'Client'}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={entry.project}
                          onChange={(e) => updateEntry(entry.id, 'project', e.target.value)}
                          disabled={entry.status !== 'draft'}
                          placeholder="No active assignments"
                          className={`w-full px-4 py-2 rounded-lg border ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-300 text-gray-900'
                          } focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50`}
                        />
                      )}
                    </div>

                    {/* Hours */}
                    <div className="md:col-span-1">
                      <label className="block text-sm font-medium mb-2">Hours</label>
                      <input
                        type="number"
                        value={entry.hours}
                        onChange={(e) => updateEntry(entry.id, 'hours', parseFloat(e.target.value))}
                        disabled={entry.status !== 'draft'}
                        min="0"
                        step="0.5"
                        className={`w-full px-4 py-2 rounded-lg border ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50`}
                      />
                    </div>

                    {/* Description */}
                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <input
                        type="text"
                        value={entry.description}
                        onChange={(e) => updateEntry(entry.id, 'description', e.target.value)}
                        disabled={entry.status !== 'draft'}
                        placeholder="What did you work on?"
                        className={`w-full px-4 py-2 rounded-lg border ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50`}
                      />
                    </div>

                    {/* Status & Actions */}
                    <div className="md:col-span-2 flex items-end space-x-2">
                      <div className="flex-1">
                        <span className={`px-3 py-2 rounded-lg text-sm font-semibold inline-block ${getStatusBadge(entry.status)}`}>
                          {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                        </span>
                      </div>
                      {entry.status === 'draft' && (
                        <button
                          onClick={() => deleteEntry(entry.id)}
                          className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
